import {
  Component,
  ViewEncapsulation,
  ViewChild,
  OnInit
} from '@angular/core';
import {
  AbstractBlStaticGuide,
  ModalReason,
  ModalButtonSetting,
  ModalButtonColor,
  BlModalContentComponent,
  ModalOption,
  ModalButtonType,
  BlModalService,
  Era,
  DateMode,
  BlDialogService,
  BlSelectComponent
} from '@blcloud/bl-ng-ui-component';
import {
  BalanceType,
  ExtractionOrganizationDiv,
} from './credit-export-condition-modal.define';
import { DateEra, DateSeparator, DateTimeOutput, DateTimePadding, DateTimeUtils } from '@blcloud/bl-common';
import {
  toNumber as _toNumber,
  defer as _defer
} from 'lodash';
import {
  CustomerInfoResourceService,
  OrganizationGuideComponent,
  OrganizationInformationResourceService
} from '@blcloud/bl-ng-share-module';
import { CustomerGuideComponent } from '@blcloud/bl-ng-vehicle-customer-module';
import { IBillingAccRecBalExtractCondition, ICustomerInfo, IOrganizationInformation } from '@blcloud/bl-datamodel';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';
import { Observable } from 'rxjs/Observable';
import { PartialObserver } from 'rxjs/Observer';

@Component({
  templateUrl: './credit-export-condition-modal.component.html',
  styleUrls: ['./credit-export-condition-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CreditExportConditionModalComponent extends AbstractBlStaticGuide implements OnInit {
  @ViewChild('content') blModalContentComponent: BlModalContentComponent;
  // 残高種別
  @ViewChild('balanceType') balanceType: BlSelectComponent;

  readonly Era = Era;

  readonly DateMode = DateMode;

  /** ガイドフッターのボタン配置 */
  footerButtonType = ModalButtonType.OkCancel;
  okButtonSetting: ModalButtonSetting = {
    color: ModalButtonColor.Success,
    label: '決定',
  };

  /** 検索条件 */
  condition: IBillingAccRecBalExtractCondition = {};

  /** 残高種別 */
  balanceTypeItems = [
    { code: BalanceType.BILLING, name: '請求' },
    { code: BalanceType.ACCREC, name: '売掛' }
  ];

  /** 抽出拠点 */
  extractionOrganizationDivItems = [
    { code: ExtractionOrganizationDiv.MANAGEMENT, name: '管理拠点' },
    { code: ExtractionOrganizationDiv.BILLING, name: '請求拠点' }
  ];

  /** 組織リソース */
  organizationResource: {};

  /** 得意先リソース */
  customerResource: {};

  constructor(
    private modalService: BlModalService,
    private dialogService: BlDialogService,
    public organizationResourceService: OrganizationInformationResourceService,
    public customerInfoResourceService: CustomerInfoResourceService,
  ) {
    super();
  }

  /**
   * 初期化処理
   */
  ngOnInit(): void {
    this.organizationResource = this.getOrganizationInnerResource();
    this.customerResource = this.getCustomerInnerResource();
  }

  /**
   * @override
   */
  getResults() {
    return {condition: this.condition};
  }

  /**
   * @override
   */
  defaultOption(): ModalOption {
    return { class: 'modelsize-middle' };
  }

  /**
   * 画面表示後にデータローディング処理
   */
  onShown() {
    this.setConditionData();
    // 初期フォーカス設定
    _defer(() => this.balanceType.focus());


  }

  /**
   * モーダルウィンドウを終了する操作が行われた際にコールされます。
   * @param reason 終了理由
   */
  dismiss(reason: ModalReason) {
    switch (reason) {
      case ModalReason.Done:
        if (this.validateCondition()) {
          this.emitDismissEvent(reason);
        }
        break;
      default:
        this.emitDismissEvent(reason);
        break;
    }
  }

  onShowGuide(id: string) {
    switch (id) {
      case 'organizationCodeStart':
        this.showOrganizationGuide('organizationCodeStart');
        break;
      case 'organizationCodeEnd':
        this.showOrganizationGuide('organizationCodeEnd');
        break;
      case 'customerCodeStart':
        this.showCustomerGuide('customerCodeStart');
        break;
      case 'customerCodeEnd':
        this.showCustomerGuide('customerCodeEnd');
        break;
      default:
        break;
    }
  }

  /**
   * 組織ガイドを表示する
   * @param propertyId 項目ID
   */
  private showOrganizationGuide(propertyId: string): void {
    this.modalService.show(OrganizationGuideComponent, {  }).subscribe(
      modalRef => {
        if (modalRef.reason === ModalReason.Done) {
          if (propertyId === 'organizationCodeStart') {
            this.condition.organizationCodeStart = modalRef.getResults() ? modalRef.getResults().organizationCode : '';
          } else {
            this.condition.organizationCodeEnd = modalRef.getResults() ? modalRef.getResults().organizationCode : '';
          }
        }
        modalRef.hide();
      }
    );
  }

  /**
   * 得意先ガイドを表示する
   * @param propertyId 項目ID
   */
  private showCustomerGuide(propertyId: string): void {
    this.modalService.show(CustomerGuideComponent, {  }).subscribe(
      modalRef => {
        if (modalRef.reason === ModalReason.Done) {
          if (propertyId === 'customerCodeStart') {
            this.condition.customerCodeStart = modalRef.getResults() ? modalRef.getResults().customerCode : '';
          } else {
            this.condition.customerCodeEnd = modalRef.getResults() ? modalRef.getResults().customerCode : '';
          }
        }
        modalRef.hide();
      }
    );
  }

  /** チェックボックス選択イベント */
  onChangeCheckboxEvent(event: any, id: string) {
    switch (id) {
      // 残高種別
      case 'creditBalanceOutputFlag':
        this.condition.creditBalanceOutputFlag = event.currentTarget.checked;
        break;
      // 抽出拠点
      case 'actualNoneOutputFlag':
        this.condition.actualNoneOutputFlag = event.currentTarget.checked;
        break;
      default:
        break;
    }
  }

  /**
   * ヘッダーデータ設定
   */
  private setConditionData() {
    const systemMonth = _toNumber(DateTimeUtils.format(
      DateTimeUtils.today(), DateTimeOutput.YM, DateTimePadding.ZERO, DateSeparator.NONE, DateEra.AD));
    this.condition = {
      balanceType: BalanceType.BILLING,
      extractionOrganizationDiv: ExtractionOrganizationDiv.MANAGEMENT,
      organizationCodeStart: undefined,
      organizationCodeEnd: undefined,
      customerCodeStart: undefined,
      customerCodeEnd: undefined,
      targetYearMonthStart: systemMonth,
      targetYearMonthEnd: systemMonth,
      creditBalanceOutputFlag: true,
      actualNoneOutputFlag: true,
    };
  }

  /**
   * 検索条件チェック
   */
  validateCondition(): boolean {
    // 拠点（Ｆｒｏｍ）
    const organizationCodeStart = this.condition.organizationCodeStart;
    // 拠点（Ｔｏ）
    const organizationCodeEnd = this.condition.organizationCodeEnd;
    // 得意先（Ｆｒｏｍ）
    const customerCodeStart = this.condition.customerCodeStart;
    // 得意先（Ｔｏ）
    const customerCodeEnd = this.condition.customerCodeEnd;
    // 対象年月（Ｆｒｏｍ）
    const targetYearMonthStart = this.condition.targetYearMonthStart === 190001 ?
      null : this.condition.targetYearMonthStart;
    // 対象年月（Ｔｏ）
    const targetYearMonthEnd = this.condition.targetYearMonthEnd === 190001 ?
      null : this.condition.targetYearMonthEnd;

    if (organizationCodeStart && organizationCodeEnd && organizationCodeStart > organizationCodeEnd) {
      const sub = this.dialogService.warn('拠点の範囲指定に誤りがあります。').subscribe((modalRef) => {
        modalRef.hide();
        sub.unsubscribe();
      });
      return false;
    }
    if (customerCodeStart && customerCodeEnd && customerCodeStart > customerCodeEnd) {
      const sub = this.dialogService.warn('得意先の範囲指定に誤りがあります。').subscribe((modalRef) => {
        modalRef.hide();
        sub.unsubscribe();
      });
      return false;
    }
    if (!targetYearMonthStart) {
      const sub = this.dialogService.warn('対象年月（Ｆｒｏｍ）を入力してください。').subscribe((modalRef) => {
        modalRef.hide();
        sub.unsubscribe();
      });
      return false;
    }
    if (!targetYearMonthEnd) {
      const sub = this.dialogService.warn('対象年月（Ｔｏ）を入力してください。').subscribe((modalRef) => {
        modalRef.hide();
        sub.unsubscribe();
      });
      return false;
    }
    if (targetYearMonthStart > targetYearMonthEnd) {
      const sub = this.dialogService.warn('開始日を終了日よりも後にすることはできません。').subscribe((modalRef) => {
        modalRef.hide();
        sub.unsubscribe();
      });
      return false;
    }

    return true;
  }

  /**
   * 組織ガイド用のリソースを取得する
   */
  private getOrganizationInnerResource(): {} {
    return {
      getById: (code: string) => {
        return Observable.create(
          (observer: PartialObserver<IOrganizationInformation>) => {
            // まず、初期取得するデータから検索
            this.organizationResourceService.getByIdUseCache(code).subscribe(
              result => {
                if (!result || result.logicalDeleteDiv === LogicalDeleteDiv.Deleted) {
                  observer.error('該当データが存在しません。');
                } else {
                  observer.next(result);
                  observer.complete();
                }
              }
            );
          }
        );
      }
    };
  }

  /**
   * 得意先ガイド用のリソースを取得する
   */
  private getCustomerInnerResource(): {} {
    return {
      getById: (code: string) => {
        return Observable.create(
          (observer: PartialObserver<ICustomerInfo>) => {
            // まず、初期取得するデータから検索
            this.customerInfoResourceService.getByIdUseCache(code).subscribe(
              result => {
                if (!result || result.logicalDeleteDiv === LogicalDeleteDiv.Deleted) {
                  observer.error('該当データが存在しません。');
                } else {
                  observer.next(result);
                  observer.complete();
                }
              }
            );
          }
        );
      }
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/export-modal/credit-export-condition-modal/credit-export-condition-modal.component.ts